import React, { FC, useReducer, useEffect, useState, CSSProperties } from "react";
import { useSelector, useDispatch } from "react-redux";
import CreatableSelect from "react-select/creatable";
import { ActionMeta, IndicatorProps } from "react-select";
import { debounce } from "throttle-debounce";
import { initialRecipientQuestionAnswer, QnAActions, recipientQuestionAnswerReducer } from "../../../reducers/recipientQuestionAnswerReducer";
import { createOption } from "./Recipient";
import { fetchComposeRecipients } from "src/actionCreators/composeActionCreators";
import { RecipientType } from "src/Core/Common/Enums";
import { IRecipientModel } from "src/Core/ViewModels/Message/RecipientModel";
import { AppNotifier } from "src/components/Common/Notification/AppNotifier";
import { getUserIdFromEmail } from "src/helper/HelperFunctions";
import { isValidEmailAddress } from "../Helper/HelperFunctions";
import { ApplicationState } from "src/store";
import { IDocumentRequestRecipient } from "src/Core/ViewModels/documentRequest/DocumentRequestModel";
import { FormLabel } from "react-bootstrap";
import { InfoCircleIcon } from "src/assets/images/svg/SideMenuIconCollection";
import { ColorConstants } from "src/assets/custom/colors";
import { IMessageSecurityQuestion } from "src/Core/ViewModels/Message/MessageSecurityQuestionModel";
import { ToasterMessages } from "src/helper/ToasterMessages";

interface ISingleRecipient {
    value?: IOption;
    setValue: (value: IDocumentRequestRecipient) => void;
    setToRecipient?: (value: IOption) => void
    inValid: boolean;
    formLabel?: string;
    formLabelInfoText?: string;
    question?: IMessageSecurityQuestion;
    answer?: string;
    questionTitle?: string;
    setQuestionUpdated?: (value: boolean) => void;
    setSelectedQuestionId?: (value: number) => void;
}

export interface IOption {
    label: string;
    value: string;
}

const SingleRecipient: FC<ISingleRecipient> = ({ setValue, inValid, setToRecipient, value, formLabel, formLabelInfoText,question,answer ,questionTitle,setQuestionUpdated,setSelectedQuestionId}) => {
    const [recipients, dispatchRecipient] = useReducer(
        recipientQuestionAnswerReducer,
        []
    );
    const dispatch = useDispatch();
    const [recipientOptions, setRecipientOptions] = useState<any[]>([]);
    const [toRecipients, setToRecipients] = useState<IOption>();
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isValueUpdated, setIsValueUpdated] = useState(false);
    const composeState = useSelector(
        (state: ApplicationState) => state.composeState
    );
    const securityQuestionState = useSelector(
        (state: ApplicationState) => state.securityQuestionState
    );

    useEffect(() => {
        getRecipientsFromItem();
    }, []);

    useEffect(() => {
        const recipientPayload = {
            email: toRecipients?.value || "",
            documentRequestRecipientId: getUserIdFromEmail(composeState.recipients.data, toRecipients?.value || ""),
            documentRequestId: 0
        };
        setValue(recipientPayload);
        setToRecipient(toRecipients);
        setIsValueUpdated(true);
        Office.context.mailbox.item.to.setAsync(
            toRecipients?.value ? [toRecipients?.value] : [],
            asyncResult => {
                if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
                    AppNotifier.Warning(ToasterMessages.WARNING.ADD_RECIPIENT);
                }
            }
        );
    }, [toRecipients]);

    useEffect(() => {
        if (recipients.length > 0) {
            updateRecipientOptions();
            if(question  && questionTitle) {
                handleRecipientQuestionAnswer(question,answer,questionTitle);
            }
            else{
                const defaultQuestion = securityQuestionState.securityQuestions.data.find(question => question.isDefault === true);
                handleRecipientQuestionAnswer(defaultQuestion);
            }     
        }
    }, [recipients.length]);

    useEffect(() => {
        !isValueUpdated && value && setToRecipients(value);
    }, [value]);

    const handleRecipientQuestionAnswer = (result: IMessageSecurityQuestion,answer?:string,questionTitle?:string) => { // Model to replaced with ISecurityQuestionAnswer
        const recipient = recipients.find(
            (x) => x.emailAddress === value?.value
        );
        if (recipient && recipient.index) {
            const data: IRecipientModel = {
                firstName: recipient.firstName,
                lastName: recipient.lastName,
                emailAddress: recipient.emailAddress,
                questionId: result.messageSecurityQuestionId,
                question:  questionTitle !== "" ? questionTitle : result.question,
                answer: answer || "",
                recipientType: RecipientType.ToRecipient,
            };

            dispatchRecipient({
                type: QnAActions.SAVE,
                payload: { data, index: recipient.index },
            });

            setValue({
                email: value?.value,
                documentRequestId: 0,
                documentRequestRecipientId: getUserIdFromEmail(composeState.recipients.data, toRecipients?.value || ""),
                question:  questionTitle !== "" ? questionTitle : result.question,
            });
        }
    };

    const handleRecipientChange = (data: any, type: RecipientType, newValue: IOption) => {
        const email = data.option?.value?.trim();
        const defaultQuestion = securityQuestionState.securityQuestions.data.find(question => question.isDefault === true);
        if(recipients.find((x:IRecipientModel) => x.emailAddress === email)) {
            return;
        }
        switch (data.action) {
            case "select-option":
                setToRecipients(newValue);
                break;
            case "create-option":
                if (isValidEmailAddress(email)) {
                    const label = isValidEmailAddress(email) &&
                        email.includes(newValue.label) ? "" : newValue.label;
                    const toData: IRecipientModel = {
                        ...initialRecipientQuestionAnswer,
                        firstName: label.split(" ")[0] ?? "",
                        lastName: label.split(" ")[1] ?? "",
                        emailAddress: email,
                        recipientType: type,
                        userId: getUserIdFromEmail(composeState.recipients.data, email),
                        questionId: defaultQuestion?.messageSecurityQuestionId,
                    };
                    setToRecipients(newValue);
                    setQuestionUpdated(false);
                    setSelectedQuestionId(defaultQuestion.messageSecurityQuestionId);
                    dispatchRecipient({ type: QnAActions.ADD, payload: toData });
                } else {
                    AppNotifier.Warning(ToasterMessages.WARNING.INVALID_EMAIL_FORMAT);
                }
                break;
            case "remove-value":
            case "clear":
                setToRecipients(newValue);
                break;
        }
    };

    const getRecipientsFromItem = () => {
        Office.context.mailbox.item.to.getAsync(asyncResult => {
            const _toRecipients = asyncResult.value;
            const _allRecipients: IRecipientModel[] = _toRecipients.map((item, index) => {
                return {
                    emailAddress: item.emailAddress,
                    firstName: item.displayName != item.emailAddress ?item.displayName :"",
                    lastName: "",
                    userId: 0,
                    recipientType: RecipientType.ToRecipient,
                    index: index,
                };
            });
            if (_allRecipients[0]) {
                dispatchRecipient({ type: QnAActions.ADD, payload: _allRecipients[0] });
            }
        });
    };
    const updateRecipientOptions = () => {
        const toRecipientsData = recipients?.map((item: IRecipientModel) => {
            const label =
                item.firstName || item.lastName
                    ? `${item.firstName} ${item.lastName} <${item.emailAddress}>`
                    : item.emailAddress;
            return createOption(`${item.emailAddress}`, label);
        });
        let i = recipients?.findIndex(x => x.emailAddress == toRecipients?.value);
        i = i < 0 ? 0 : i;
        setToRecipients(toRecipientsData[i]);
    };

    const handleInputChange = (inputValue: string) => {
        if (inputValue.length < 3) return;
        else {
            setLoading(true);
            dispatch(
                fetchComposeRecipients(inputValue, (data: IRecipientModel[]) => {
                    const options = data.map((item: IRecipientModel) =>
                        createOption(
                            `${item.emailAddress}`,
                            item.firstName || item.lastName
                                ? `${item.firstName ?? ""} ${item.lastName ?? ""} <${item.emailAddress}>`
                                : item.emailAddress
                        )
                    );
                    setRecipientOptions(options);
                    setLoading(false);
                })
            );
        }
    };

    const handleToChange = (newValue: any, actionMeta: ActionMeta<any>) => {
        handleRecipientChange(actionMeta, RecipientType.ToRecipient, newValue);
    };

    const ClearIndicator = (props: IndicatorProps<IOption, true>) => {
        const {
            children = <div className="clear-icon"><i className="fas fa-times"></i></div>,
            getStyles,
            innerProps: { ref, ...restInnerProps },
        } = props;
        return (
            <div
                {...restInnerProps}
                ref={ref}
                style={getStyles("clearIndicator", props) as CSSProperties}
            >
                <div style={{ padding: "0px 5px" }}>{children}</div>
            </div>
        );
    };
    const defaultCreatableStyle = {
        control: (base: any) => ({
            ...base,
            minHeight: "29px",
            lineHeight: "normal",
            fontSize: "14px",
            borderRadius: "3px",
            borderColor: !inValid ? "#ced4da" : "#dc3545"
        }),
        menu: (base: any) => ({
            ...base,
            fontSize: "12px",
        }),
    };
    return (
        <>
            <div className="to-label-wrapper">
                <FormLabel className="compose-msg-title">{formLabel}</FormLabel>
                <span title={formLabelInfoText} >
                    <InfoCircleIcon width={12} height={12} color={ColorConstants.brand_light_blue} />
                </span>
            </div>
            <div>
                <CreatableSelect
                    className="single-recipient-select"
                    isClearable
                    cacheOptions
                    placeholder=""
                    blurInputOnSelect
                    hideSelectedOptions={false}
                    allowCreateWhileLoading={true}
                    options={recipientOptions}
                    onChange={handleToChange}
                    onInputChange={debounce(1000, handleInputChange)}
                    formatCreateLabel={(value: any) => value}
                    theme={(theme) => {
                        return { ...theme, borderRadius: 0 };
                    }}
                    components={{
                        DropdownIndicator: null,
                        ClearIndicator
                    }}
                    styles={defaultCreatableStyle}
                    value={toRecipients}
                    isLoading={isLoading}
                />
            </div>
        </>

    );
};

export default SingleRecipient;


